"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { useLocale } from "next-intl";
import { Reveal } from "@/components/ui/Reveal";
import { SectionHeading } from "@/components/ui/SectionHeading";
import {
  ChurchIcon,
  MountainIcon,
  TownIcon,
  TrailIcon,
  ArrowIcon,
} from "@/components/icons/UiIcons";
import type { AttractionCategory } from "@/data/discover/types";
import { AttractionCard } from "./AttractionCard";
import { LazyMount } from "./LazyMount";
import type { CategoryLabel, ExplorerLabels, PlaceCard } from "./shared";

const AttractionMap = dynamic(() => import("./AttractionMap"), { ssr: false });

type Filter = AttractionCategory | "all";

const CATEGORY_ICON: Record<AttractionCategory, typeof MountainIcon> = {
  nature: MountainIcon,
  culture: ChurchIcon,
  town: TownIcon,
  adventure: TrailIcon,
};

/**
 * The interactive heart of /discover: category cards, filter chips, the
 * place grid and the map share one `filter` state, so a category card click,
 * a chip, a marker and the list all stay in sync. Everything renders on the
 * server with filter = "all", so the HTML is complete without JavaScript.
 */
export function DiscoverExplorer({
  places,
  categories,
  labels,
}: {
  places: PlaceCard[];
  categories: CategoryLabel[];
  labels: ExplorerLabels;
}) {
  const locale = useLocale();
  const [filter, setFilter] = useState<Filter>("all");
  const [selected, setSelected] = useState<string | null>(null);
  const [mobileView, setMobileView] = useState<"list" | "map">("list");

  // Deep links: /discover#culture pre-selects a category (also used by the
  // homepage category links). Read after mount — the hash is client-only.
  useEffect(() => {
    const h = window.location.hash.replace("#", "");
    if (categories.some((c) => c.id === h)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time URL sync
      setFilter(h as AttractionCategory);
    }
  }, [categories]);

  const visible = useMemo(
    () => (filter === "all" ? places : places.filter((p) => p.category === filter)),
    [filter, places],
  );
  const pinned = useMemo(() => visible.filter((p) => p.lat != null), [visible]);

  const colors = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c.color])),
    [categories],
  );
  const names = useMemo(
    () => Object.fromEntries(categories.map((c) => [c.id, c.name])),
    [categories],
  );

  function choose(next: Filter, scroll = false) {
    setFilter(next);
    setSelected(null);
    if (scroll) {
      document
        .getElementById("places")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  return (
    <>
      {/* ── Categories ─────────────────────────────────────────── */}
      <section
        id="categories"
        className="mx-auto max-w-6xl scroll-mt-24 px-4 py-16 sm:px-6 sm:py-20"
      >
        <Reveal>
          <SectionHeading
            eyebrow={labels.categoriesEyebrow}
            title={labels.categoriesTitle}
            subtitle={labels.categoriesSubtitle}
          />
        </Reveal>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {categories.map((c, i) => {
            const Icon = CATEGORY_ICON[c.id];
            const active = filter === c.id;
            return (
              <Reveal key={c.id} delay={i * 70}>
                <button
                  type="button"
                  onClick={() => choose(active ? "all" : c.id, true)}
                  aria-pressed={active}
                  className={`card-lift group relative flex h-full w-full flex-col overflow-hidden rounded-2xl border text-left transition-colors ${
                    active ? "border-forest-400 ring-2 ring-forest-400/40" : "border-sand"
                  } bg-cream`}
                >
                  <div className="relative aspect-[5/3] overflow-hidden">
                    <Image
                      src={c.image}
                      alt={c.name}
                      fill
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                      className="img-zoom object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-forest-deep/70 via-forest-deep/10 to-transparent" />
                    <span
                      className="absolute bottom-3 left-3 grid h-10 w-10 place-items-center rounded-full bg-white/90 text-[#173f35] shadow-sm backdrop-blur"
                      aria-hidden
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-display text-lg font-bold text-bark">
                      {c.name}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-stone">
                      {c.description}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-forest-600">
                      {labels.explore}
                      <ArrowIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </button>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ── Places + filter ────────────────────────────────────── */}
      <section
        id="places"
        className="scroll-mt-20 border-y border-sand/70 bg-cream/60"
      >
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
            <Reveal>
              <SectionHeading
                eyebrow={labels.placesEyebrow}
                title={labels.placesTitle}
                subtitle={labels.placesSubtitle}
              />
            </Reveal>
            <div
              role="group"
              aria-label={labels.filterLabel}
              className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0"
            >
              <Chip
                active={filter === "all"}
                onClick={() => choose("all")}
                label={labels.filterAll}
              />
              {categories.map((c) => (
                <Chip
                  key={c.id}
                  active={filter === c.id}
                  onClick={() => choose(c.id)}
                  label={c.name}
                  color={c.color}
                />
              ))}
            </div>
          </div>

          <p className="mt-6 text-sm text-stone" aria-live="polite">
            {labels.resultsCounts[visible.length] ?? String(visible.length)}
          </p>

          {visible.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed border-sand bg-cream px-6 py-14 text-center">
              <p className="text-stone">{labels.empty}</p>
              <button
                type="button"
                onClick={() => choose("all")}
                className="mt-4 text-sm font-semibold text-forest-600 hover:underline"
              >
                {labels.emptyAction}
              </button>
            </div>
          ) : (
            <ul className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((p, i) => (
                <li key={p.slug} className="h-full">
                  <AttractionCard
                    place={p}
                    categoryName={names[p.category]}
                    categoryColor={colors[p.category]}
                    exploreLabel={labels.explore}
                    visitLabel={labels.visitTime}
                    priority={i < 3}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ── Map ────────────────────────────────────────────────── */}
      <section id="map" className="mx-auto max-w-6xl scroll-mt-24 px-4 py-16 sm:px-6 sm:py-20">
        <Reveal>
          <SectionHeading
            eyebrow={labels.mapEyebrow}
            title={labels.mapTitle}
            subtitle={labels.mapSubtitle}
          />
        </Reveal>

        {/* Mobile: list / map toggle */}
        <div
          role="tablist"
          aria-label={labels.mapTitle}
          className="mt-8 inline-flex rounded-full border border-sand bg-cream p-1 lg:hidden"
        >
          {(["list", "map"] as const).map((v) => (
            <button
              key={v}
              role="tab"
              type="button"
              aria-selected={mobileView === v}
              onClick={() => setMobileView(v)}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                mobileView === v
                  ? "bg-forest-500 text-white"
                  : "text-stone hover:text-forest-600"
              }`}
            >
              {v === "list" ? labels.showList : labels.showMap}
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-6 lg:mt-10 lg:grid-cols-[minmax(0,320px)_1fr]">
          {/* Compact list */}
          <div
            className={`max-h-[560px] space-y-1 overflow-y-auto rounded-2xl border border-sand bg-cream/70 p-2 ${
              mobileView === "map" ? "hidden lg:block" : ""
            }`}
          >
            {pinned.length === 0 && (
              <p className="px-3 py-6 text-sm text-stone">{labels.mapEmpty}</p>
            )}
            {pinned.map((p) => (
              <div
                key={p.slug}
                className="relative"
                onClick={() => setSelected(p.slug)}
                onFocus={() => setSelected(p.slug)}
              >
                <AttractionCard
                  compact
                  place={p}
                  categoryName={names[p.category]}
                  categoryColor={colors[p.category]}
                  exploreLabel={labels.explore}
                  visitLabel={labels.visitTime}
                  selected={selected === p.slug}
                />
              </div>
            ))}
            {visible.length > pinned.length && (
              <p className="px-3 pb-2 pt-3 text-xs text-stone">{labels.mapNoCoords}</p>
            )}
          </div>

          {/* Map */}
          <div
            className={`relative h-[420px] overflow-hidden rounded-2xl border border-sand bg-sand/40 lg:h-[560px] ${
              mobileView === "list" ? "hidden lg:block" : ""
            }`}
          >
            <LazyMount
              className="h-full w-full"
              placeholder={
                <div className="grid h-full w-full place-items-center text-sm text-bark/70">
                  {labels.mapLoading}
                </div>
              }
            >
              <AttractionMap
                places={pinned}
                colors={colors}
                categoryNames={names}
                localePrefix={`/${locale}`}
                selected={selected}
                onSelect={setSelected}
                openLabel={labels.openPlace}
                ariaLabel={labels.mapAria}
              />
            </LazyMount>
          </div>
        </div>
      </section>
    </>
  );
}

function Chip({
  active,
  onClick,
  label,
  color,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  color?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
        active
          ? "border-forest-500 bg-forest-500 text-white"
          : "border-sand bg-cream text-bark hover:border-forest-400 hover:text-forest-600"
      }`}
    >
      {color && (
        <span
          className="h-2 w-2 rounded-full"
          style={{ backgroundColor: active ? "#fff" : color }}
          aria-hidden
        />
      )}
      {label}
    </button>
  );
}
